import { useState, useEffect } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { fetchData, postData, SyncData } from '../../../apiService';
import {
  IconButton,
  Button,
  Chip,
  Badge,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Collapse from '@mui/material/Collapse';
import RemoveIcon from '@mui/icons-material/Remove';
import InfoIcon from '@mui/icons-material/Info';
import AddIcon from '@mui/icons-material/Add';
import RestaurantMenu from '@mui/icons-material/Restaurant';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import LocalDiningIcon from '@mui/icons-material/LocalDining';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import SendIcon from '@mui/icons-material/Send';
import PhoneIcon from '@mui/icons-material/Phone';
import DescriptionModal from './DescriptionModal';
import { generateKOTPDF } from '../KOT/Estimate';
import { useAuth } from '../../../AuthContext';
import { tokenIsExpired } from '../../../authUtils';
import CircularProgress from '@mui/material/CircularProgress';

const CreateKOT = () => {
  const { id } = useParams();
  const history = useHistory();
  const { isAuthenticated, companyId, userId, token, logout } = useAuth();
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (isAuthenticated && tokenIsExpired(token)) {
      logout();
    }
  }, [isAuthenticated, companyId, token, logout]);

  const [expandedCategories, setExpandedCategories] = useState([]);
  const handleExpandClick = (categoryName) => {
    setExpandedCategories((prevExpanded) => {
      if (prevExpanded.includes(categoryName)) {
        return prevExpanded.filter((cat) => cat !== categoryName);
      } else {
        return [...prevExpanded, categoryName];
      }
    });
  };

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedItemId, setSelectedItemId] = useState(null);
  const [description, setDescription] = useState('');

  const saveDescription = (newDescription) => {
    setSelectedItems((prevItems) =>
      prevItems.map((item) =>
        item.ItemId === selectedItemId
          ? { ...item, description: newDescription }
          : item
      )
    );

    setIsModalOpen(false);
  };

  const [totalQuantity, setTotalQuantity] = useState(0);
  const [kotDate, setKOTDate] = useState('');
  const [kotTime, setKOTTime] = useState('');
  const [tableName, setTableName] = useState('');
  const [tableId, setTableId] = useState('');
  const [showContact, setShowContact] = useState(false);
  const [contactDetails, setContactDetails] = useState('');

  const currentDate = new Date();
  const formattedDate = currentDate.toISOString().split('T')[0];
  const formattedTime = currentDate.toTimeString().split(' ')[0];

  const [categoryData, setCategoryData] = useState([]);
  const [selectedItems, setSelectedItems] = useState([]);

  useEffect(() => {
    fetchData('KOTApi', `/ItemTableDetails/${companyId}`, token)
      .then((response) => {
        if (response.responseCode === '1111') {
          setCategoryData(response.response_data);
        }
      })
      .catch((error) => {
        console.error('Error fetching Category Data:', error);
      });

    fetchData('TableNameApi', `/TableNameDetails/${id}`, token)
      .then((response) => {
        if (response.responseCode === '1111') {
          setTableId(response.response_data.Id);
          setTableName(response.response_data.Name);
          setKOTDate(formattedDate);
          setKOTTime(formattedTime);
        }
      })
      .catch((error) => {
        console.error('Error fetching Table Name Details:', error);
      });
  }, [id, formattedDate, formattedTime]);

  const handleIncrement = (CategoryId, ItemId, itemName) => {
    setSelectedItems((prevItems) => {
      const existingItem = prevItems.find((item) => item.ItemId === ItemId);
      if (existingItem) {
        // If the item is already in the list, update its quantity
        setTotalQuantity((prevTotal) => prevTotal + 1);
        return prevItems.map((item) =>
          item.ItemId === ItemId ? { ...item, Quantity: item.Quantity + 1 } : item
        );
      } else {
        // If the item is not in the list, add it with quantity 1
        setTotalQuantity((prevTotal) => prevTotal + 1);
        return [...prevItems, { CategoryId, ItemId, Quantity: 1, itemName }];
      }
    });
  };

  const handleDecrement = (ItemId) => {
    setSelectedItems((prevItems) => {
      const existingItem = prevItems.find((item) => item.ItemId === ItemId);
      if (existingItem) {
        setTotalQuantity((prevTotal) => Math.max(prevTotal - 1, 0)); // Decrement total quantity
        // If the item is already in the list, update its quantity
        return prevItems.map((item) =>
          item.ItemId === ItemId ? { ...item, Quantity: Math.max(item.Quantity - 1, 0) } : item
        );
      } else {
        // If the item is not in the list, do nothing
        return prevItems;
      }
    });
  };

  const getTotalQuantityForCategory = (categoryId) => {
    return selectedItems
      .filter((item) => item.CategoryId === categoryId)
      .reduce((total, item) => total + item.Quantity, 0);
  };

  const submitData = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    const kotdata = {
      TableId: tableId,
      CreatedBy: userId,
      CompanyId: companyId,
      CreatedDate: new Date(),
      Description: contactDetails,
    };

    const CreateData = {
      KOT: kotdata,
      KOTDetails: selectedItems
    };

    postData('KOTApi', CreateData, token)
      .then((response) => {
        console.log('KOT Created success:', response);
        setIsLoading(false);
        if (response.response === '1111' && companyId != 4) {
          const kotid = response.kotId;
          handleKOTPrint(kotid);
        }

        history.push('/Home');
      })
      .catch((error) => {
        console.error('KOT Create error:', error);
        setIsLoading(false);
      });

  };

  const handleKOTPrint = async (kotid) => {
    try {
      const response = await fetchData('KOTApi', `/KOTPrint/${kotid}`, token);
      const labelData = response.response_data;
      await generateKOTPDF(labelData);

      try {
        SyncData('KOTApi', `${kotid}`, token);
      } catch (updateError) {
        console.error('Error updating record:', updateError);
      }
    } catch (error) {
      console.error('Error printing Estimate:', error);
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: '#f5f5f0',
      padding: '12px',
      paddingBottom: totalQuantity > 0 ? '80px' : '12px'
    }}>
      <style>
        {`
          .menu-container {
            max-width: 1200px;
            margin: 0 auto;
          }

          .menu-header {
            background: linear-gradient(135deg, #f6f8f2 0%, #edf1e6 100%);
            border: 1px solid rgba(106, 130, 59, 0.15);
            border-left: 4px solid #6a823b;
            border-radius: 12px;
            padding: 10px 16px;
            margin-bottom: 16px;
            box-shadow: 0 4px 12px rgba(106, 130, 59, 0.08);
            color: #2e3a1f;
          }

          .menu-title {
            font-size: 16px;
            font-weight: 700;
            color: #2e3a1f;
            margin: 0;
            display: flex;
            align-items: center;
            gap: 8px;
            white-space: nowrap;
          }

          .menu-title svg {
            font-size: 18px;
            color: #6a823b;
          }

          .menu-subtitle {
            color: #5d6b4f;
            font-size: 11px;
            margin-top: 0;
            white-space: nowrap;
          }

          .category-card {
            background: #ffffff;
            border: 1px solid #e0e0e0;
            border-radius: 10px;
            margin-bottom: 10px;
            overflow: hidden;
            box-shadow: 0 2px 6px rgba(0, 0, 0, 0.06);
            transition: all 0.3s ease;
          }

          .category-card:hover {
            box-shadow: 0 4px 10px rgba(0, 0, 0, 0.1);
          }

          .category-header {
            padding: 10px 14px;
            cursor: pointer;
            display: flex;
            align-items: center;
            justify-content: space-between;
            background: #fafafa;
            transition: all 0.3s ease;
            border-bottom: 1px solid #e0e0e0;
          }

          .category-header:hover {
            background: #f0f0f0;
          }

          .category-name {
            font-size: 15px;
            font-weight: 700;
            color: #2c3e50;
            display: flex;
            align-items: center;
            gap: 7px;
          }

          .category-header svg {
            font-size: 18px;
          }

          .category-badge {
            background: linear-gradient(135deg, #fc4a1a 0%, #f7b733 100%);
            color: white;
            padding: 2px 8px;
            border-radius: 8px;
            font-size: 10px;
            font-weight: 700;
            box-shadow: 0 1px 4px rgba(252, 74, 26, 0.3);
          }

          .menu-item-row {
            padding: 8px 14px;
            border-bottom: 1px solid #f0f0f0;
            display: flex;
            align-items: center;
            justify-content: space-between;
            transition: all 0.3s ease;
          }

          .menu-item-row:last-child {
            border-bottom: none;
          }

          .menu-item-row:hover {
            background: #fafafa;
          }

          .menu-item-name {
            font-size: 13px;
            font-weight: 600;
            color: #34495e;
            flex: 1;
          }

          .quantity-controls {
            display: flex;
            align-items: center;
            gap: 7px;
            background: #f8f9fa;
            padding: 3px 8px;
            border-radius: 8px;
            border: 1px solid #e0e0e0;
          }

          .quantity-btn {
            background: linear-gradient(135deg, #56ab2f 0%, #a8e063 100%);
            color: white;
            border: none;
            width: 24px;
            height: 24px;
            border-radius: 6px;
            display: flex;
            align-items: center;
            justify-content: center;
            cursor: pointer;
            transition: all 0.3s ease;
            box-shadow: 0 1px 4px rgba(86, 171, 47, 0.3);
          }

          .quantity-btn:hover {
            transform: scale(1.1);
            box-shadow: 0 3px 10px rgba(86, 171, 47, 0.5);
          }

          .quantity-btn.remove {
            background: linear-gradient(135deg, #fc4a1a 0%, #f7b733 100%);
            box-shadow: 0 2px 6px rgba(252, 74, 26, 0.3);
          }

          .quantity-btn.remove:hover {
            box-shadow: 0 2px 8px rgba(252, 74, 26, 0.4);
          }

          .quantity-display {
            font-size: 14px;
            font-weight: 700;
            color: #2c3e50;
            min-width: 22px;
            text-align: center;
          }

          .info-btn {
            background: #e3f2fd;
            border: 1px solid #90caf9;
            color: #1976d2;
            width: 26px;
            height: 26px;
            border-radius: 6px;
            display: flex;
            align-items: center;
            justify-content: center;
            cursor: pointer;
            transition: all 0.3s ease;
            margin-left: 5px;
          }

          .info-btn:hover {
            background: #bbdefb;
            transform: scale(1.05);
          }

          .cart-footer {
            position: fixed;
            bottom: 0;
            left: 0;
            right: 0;
            background: #ffffff;
            border-top: 1px solid #e0e0e0;
            padding: 10px 14px;
            box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.08);
            z-index: 1000;
          }

          .cart-content {
            max-width: 1200px;
            margin: 0 auto;
            display: flex;
            align-items: center;
            justify-content: space-between;
            gap: 10px;
          }

          .cart-info {
            display: flex;
            align-items: center;
            gap: 10px;
            flex: 1;
          }

          .cart-icon-wrapper {
            background: linear-gradient(135deg, #fc4a1a 0%, #f7b733 100%);
            width: 36px;
            height: 36px;
            border-radius: 8px;
            display: flex;
            align-items: center;
            justify-content: center;
            box-shadow: 0 2px 8px rgba(252, 74, 26, 0.3);
          }

          .cart-details {
            color: #2c3e50;
          }

          .cart-items-count {
            font-size: 18px;
            font-weight: 800;
            color: #2c3e50;
          }

          .cart-label {
            font-size: 10px;
            color: #7f8c8d;
            font-weight: 600;
          }

          .send-btn {
            background: linear-gradient(135deg, #56ab2f 0%, #a8e063 100%);
            color: white;
            border: none;
            padding: 8px 20px;
            border-radius: 8px;
            font-size: 13px;
            font-weight: 700;
            cursor: pointer;
            display: flex;
            align-items: center;
            gap: 6px;
            box-shadow: 0 2px 8px rgba(86, 171, 47, 0.3);
            transition: all 0.3s ease;
            text-transform: none;
          }

          .send-btn:hover {
            transform: translateY(-1px);
            box-shadow: 0 4px 12px rgba(86, 171, 47, 0.4);
          }

          .send-btn:disabled {
            opacity: 0.6;
            cursor: not-allowed;
          }

          .expand-icon {
            transition: transform 0.3s ease;
            color: #7f8c8d;
            font-size: 20px;
          }

          .expand-icon.expanded {
            transform: rotate(180deg);
          }

          .cart-icon-wrapper svg {
            font-size: 20px;
          }

          .send-btn svg {
            font-size: 16px;
          }

          /* Tablet - 768px and below */
          @media (max-width: 768px) {
            .menu-container {
              padding: 0 8px;
            }

            .menu-header {
              padding: 10px 14px;
              margin-bottom: 12px;
            }

            .menu-title {
              font-size: 17px;
            }

            .menu-title svg {
              font-size: 18px;
            }

            .menu-subtitle {
              font-size: 11px;
            }

            .category-card {
              margin-bottom: 10px;
            }

            .category-header {
              padding: 10px 14px;
            }

            .category-name {
              font-size: 14px;
              gap: 6px;
            }

            .category-header svg {
              font-size: 16px;
            }

            .menu-item-row {
              padding: 8px 12px;
            }

            .menu-item-name {
              font-size: 13px;
            }

            .quantity-controls {
              gap: 6px;
              padding: 3px 7px;
            }

            .quantity-btn {
              width: 24px;
              height: 24px;
            }

            .quantity-display {
              font-size: 13px;
              min-width: 20px;
            }

            .info-btn {
              width: 26px;
              height: 26px;
              margin-left: 4px;
            }

            .cart-footer {
              padding: 10px 12px;
            }

            .cart-icon-wrapper {
              width: 36px;
              height: 36px;
            }

            .cart-items-count {
              font-size: 17px;
            }

            .cart-label {
              font-size: 10px;
            }

            .send-btn {
              padding: 8px 18px;
              font-size: 12px;
              gap: 5px;
            }
          }

          /* Phone - 480px and below */
          @media (max-width: 480px) {
            .menu-container {
              padding: 0;
            }

            .menu-header {
              padding: 8px 12px;
              margin-bottom: 10px;
              border-radius: 10px;
            }

            .menu-title {
              font-size: 15px;
              gap: 6px;
            }

            .menu-title svg {
              font-size: 16px;
            }

            .menu-subtitle {
              font-size: 10px;
              margin-top: 2px;
            }

            .category-card {
              margin-bottom: 8px;
              border-radius: 8px;
            }

            .category-header {
              padding: 8px 12px;
            }

            .category-name {
              font-size: 13px;
              gap: 5px;
            }

            .category-header svg {
              font-size: 15px;
            }

            .category-badge {
              font-size: 9px;
              padding: 2px 6px;
            }

            .menu-item-row {
              padding: 10px 12px;
              flex-direction: row;
              flex-wrap: wrap;
              gap: 8px;
            }

            .menu-item-name {
              font-size: 12px;
              width: 100%;
              margin-bottom: 4px;
            }

            .quantity-controls {
              flex: 1;
              min-width: 120px;
              justify-content: space-between;
              gap: 8px;
              padding: 4px 8px;
            }

            .quantity-btn {
              width: 26px;
              height: 26px;
            }

            .quantity-display {
              font-size: 13px;
              min-width: 24px;
            }

            .info-btn {
              width: 26px;
              height: 26px;
              margin-left: 0;
            }

            .expand-icon {
              font-size: 18px;
            }

            .cart-footer {
              padding: 8px 10px;
            }

            .cart-content {
              gap: 8px;
            }

            .cart-info {
              gap: 8px;
            }

            .cart-icon-wrapper {
              width: 32px;
              height: 32px;
            }

            .cart-icon-wrapper svg {
              font-size: 18px;
            }

            .cart-items-count {
              font-size: 16px;
            }

            .cart-label {
              font-size: 9px;
            }

            .send-btn {
              padding: 8px 16px;
              font-size: 12px;
              gap: 4px;
              white-space: nowrap;
            }

            .send-btn svg {
              font-size: 14px;
            }
          }

          /* Extra small phones - 360px and below */
          @media (max-width: 360px) {
            .menu-header {
              padding: 6px 10px;
            }

            .menu-title {
              font-size: 14px;
            }

            .menu-title svg {
              font-size: 15px;
            }

            .category-name {
              font-size: 12px;
            }

            .category-header svg {
              font-size: 14px;
            }

            .menu-item-name {
              font-size: 11px;
            }

            .quantity-controls {
              min-width: 100px;
            }

            .quantity-btn {
              width: 24px;
              height: 24px;
            }

            .quantity-display {
              font-size: 12px;
            }

            .cart-items-count {
              font-size: 14px;
            }

            .send-btn {
              padding: 7px 12px;
              font-size: 11px;
            }
          }
        `}
      </style>
      <form onSubmit={submitData} className="menu-container">
        {isModalOpen && (
          <DescriptionModal
            isOpen={isModalOpen}
            onClose={() => setIsModalOpen(false)}
            itemId={selectedItemId}
            description={description}
            onSave={saveDescription}
          />
        )}

        <div className="menu-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'nowrap' }}>
            <h1 className="menu-title" style={{ flexShrink: 0 }}>
              <RestaurantIcon />
              {tableName}
            </h1>
            <IconButton
              size="small"
              onClick={() => setShowContact((v) => !v)}
              title="Contact Details"
              style={{
                flexShrink: 0,
                background: showContact ? 'rgba(106,130,59,0.15)' : 'transparent',
                border: '1px solid rgba(106,130,59,0.3)',
                borderRadius: '6px',
                padding: '3px',
                color: '#6a823b',
              }}
            >
              <PhoneIcon style={{ fontSize: 15 }} />
            </IconButton>
            {showContact && (
              <input
                autoFocus
                type="text"
                placeholder="Contact (Phone / Name)"
                value={contactDetails}
                onChange={(e) => setContactDetails(e.target.value)}
                style={{
                  flex: 1,
                  padding: '4px 8px',
                  borderRadius: '6px',
                  border: '1px solid rgba(106,130,59,0.3)',
                  fontSize: '11px',
                  background: '#fff',
                  color: '#2e3a1f',
                  outline: 'none',
                  minWidth: 0,
                }}
              />
            )}
            <div className="menu-subtitle" style={{ fontWeight: 600, fontSize: '11px', flexShrink: 0 }}>
              {kotDate} • {kotTime}
            </div>
          </div>
        </div>
        {categoryData.map((category) => (
          <div key={category.CategoryName} className="category-card">
            <div
              className="category-header"
              onClick={() => handleExpandClick(category.CategoryName)}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flex: 1 }}>
                <LocalDiningIcon style={{ color: '#56ab2f' }} />
                <span className="category-name">{category.CategoryName}</span>
              </div>

              {getTotalQuantityForCategory(category.CategoryId) > 0 && (
                <Badge
                  badgeContent={getTotalQuantityForCategory(category.CategoryId)}
                  sx={{
                    '& .MuiBadge-badge': {
                      background: 'linear-gradient(135deg, #fc4a1a 0%, #f7b733 100%)',
                      color: 'white',
                      fontWeight: 700,
                      fontSize: '11px',
                      padding: '0 6px',
                      height: '20px',
                      minWidth: '20px',
                      borderRadius: '10px'
                    }
                  }}
                >
                  <ShoppingCartIcon style={{ color: '#f7b733' }} />
                </Badge>
              )}

              <ExpandMoreIcon
                className={`expand-icon ${expandedCategories.includes(category.CategoryName) ? 'expanded' : ''}`}
              />
            </div>

            <Collapse in={expandedCategories.includes(category.CategoryName)} timeout="auto" unmountOnExit>
              <div>
                {category.Items.map((item) => {
                  const quantity = selectedItems.find((selectedItem) => selectedItem.ItemId === item.Id)?.Quantity || 0;
                  return (
                    <div key={item.Id} className="menu-item-row">
                      <div className="menu-item-name">{item.Name}</div>

                      <div style={{ display: 'flex', alignItems: 'center' }}>
                        <div className="quantity-controls">
                          {quantity > 0 && (
                            <IconButton
                              size="small"
                              onClick={() => {
                                setSelectedItemId(item.Id);
                                setDescription(selectedItems.find((selectedItem) => selectedItem.ItemId === item.Id)?.description || '');
                                setIsModalOpen(true);
                              }}
                              style={{ padding: 0 }}
                            >
                              <div className="info-btn">
                                <InfoIcon style={{ fontSize: 16 }} />
                              </div>
                            </IconButton>
                          )}

                          <IconButton
                            size="small"
                            onClick={() => handleDecrement(item.Id)}
                            style={{ padding: 0, marginLeft: '10px' }}
                          >
                            <div className="quantity-btn remove">
                              <RemoveIcon style={{ fontSize: 16 }} />
                            </div>
                          </IconButton>

                          <span className="quantity-display">{quantity}</span>

                          <IconButton
                            size="small"
                            onClick={() => handleIncrement(item.CategoryId, item.Id, item.Name)}
                            style={{ padding: 0 }}
                          >
                            <div className="quantity-btn">
                              <AddIcon style={{ fontSize: 16 }} />
                            </div>
                          </IconButton>
                        </div>


                      </div>
                    </div>
                  );
                })}
              </div>
            </Collapse>
          </div>
        ))}
      </form>

      {totalQuantity > 0 && (
        <div className="cart-footer">
          <div className="cart-content">
            <div className="cart-info">
              <div className="cart-icon-wrapper">
                <RestaurantMenu style={{ color: 'white' }} />
              </div>
              <div className="cart-details">
                <div className="cart-items-count">{totalQuantity}</div>
                <div className="cart-label">ITEMS SELECTED</div>
              </div>
            </div>

            <Button
              className="send-btn"
              onClick={submitData}
              disabled={isLoading}
            >
              {isLoading ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                <>
                  <SendIcon />
                  Send to Kitchen
                </>
              )}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CreateKOT;
